// ** Modules
import React from 'react';

// ** UI
import Fab from '@mui/material/Fab';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box, { type BoxProps } from '@mui/material/Box';
import { styled /* useTheme */ } from '@mui/material/styles';

// ** Icons
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

// ** Components
import CustomAppBar from './navMenu/components/CustomAppBar';
import VerticalNavigation from './navMenu/VerticalNavigation';

// ** Layout
import Footer from '@/components/layout/Footer';
import BackToTopButton from '@/components/layout/BackToTopButton';
import ScrollToTop from '@/components/layout/ScrollToTop';

// ** Styles
import DatePickerWrapper from '@/styles/datePickerWrapper';

// ** Theme
import themeConfig from '@/theme/themeConfig';

// ** Types
import type { LayoutProps, VerticalLayoutProps } from '@/types/layoutProps';

type VerticalLayoutPropsInternal = LayoutProps & {
	verticalLayoutProps: VerticalLayoutProps;
};

// ** Styled Components
const VerticalLayoutWrapper = styled('div')({
	height: '100%',
	display: 'flex'
});

const MainContentWrapper = styled(Box)<BoxProps & { path: string }>(({ theme, path }) => ({
	flexGrow: 1,
	minWidth: 0,
	display: 'flex',
	minHeight: '100vh',
	flexDirection: 'column',
	background: path.startsWith('/user') ? theme.palette.grey[100] : 'transparent'
}));

const ContentWrapper = styled('main')({
	flexGrow: 1,
	width: '100%',
	overflowX: 'clip',
	transition: 'padding .25s ease-in-out'
});

const VerticalLayout = ({
	hidden,
	settings,
	children,
	scrollToTop,
	currentUser,
	verticalLayoutProps,
	host,
	footerProps,
	...props
}: VerticalLayoutPropsInternal) => {
	// ** Vars
	const { skin, navHidden, contentWidth } = settings;
	const { navigationSize, collapsedNavigationSize } = themeConfig;
	const navWidth = navigationSize;
	const navigationBorderWidth = skin === 'bordered' ? 1 : 0;
	const collapsedNavWidth = collapsedNavigationSize;
	const isDesktop = useMediaQuery('(min-width:992px)');

	// ** States
	const [navVisible, setNavVisible] = React.useState<boolean>(isDesktop);

	// ** Handlers
	const toggleNavVisibility = () => setNavVisible(!navVisible);

	// ** Get current path
	const path = window.location.pathname;

	return (
		<React.Fragment>
			<VerticalLayoutWrapper>
				{/* Navigation Menu */}
				{navHidden && !(navHidden && settings.lastLayout === 'verticalNavigation') ? null : (
					<VerticalNavigation
						hidden={hidden}
						settings={settings}
						host={host}
						navWidth={navWidth}
						navVisible={navVisible}
						setNavVisible={setNavVisible}
						collapsedNavWidth={collapsedNavWidth}
						toggleNavVisibility={toggleNavVisibility}
						navigationBorderWidth={navigationBorderWidth}
						verticalNavMenuContent={verticalLayoutProps.navMenu.content}
						navMenuBranding={verticalLayoutProps.navMenu.branding}
						menuLockedIcon={verticalLayoutProps.navMenu.lockedIcon}
						verticalNavItems={verticalLayoutProps.navMenu.navItems}
						navMenuProps={verticalLayoutProps.navMenu.componentProps}
						menuUnlockedIcon={verticalLayoutProps.navMenu.unlockedIcon}
						afterVerticalNavMenuContent={verticalLayoutProps.navMenu.afterContent}
						beforeVerticalNavMenuContent={verticalLayoutProps.navMenu.beforeContent}
						{...props}
					/>
				)}

				{/* Main Content */}
				<MainContentWrapper path={path}>
					{/* AppBar Component */}
					<CustomAppBar
						toggleNavVisibility={toggleNavVisibility}
						appBarContent={verticalLayoutProps.appBar?.content}
						appBarProps={verticalLayoutProps.appBar?.componentProps}
						currentUser={currentUser}
						settings={settings}
						path={path}
						{...props}
					/>

					{/* Content */}
					<ContentWrapper
						sx={{
							...(contentWidth === 'boxed' && {
								mx: 'auto',
								'@media (min-width:1440px)': { maxWidth: 1440 },
								'@media (min-width:1200px)': { maxWidth: '100%' }
							})
						}}
					>
						{children}
					</ContentWrapper>

					<BackToTopButton />

					{/* Footer Component */}
					<Footer
						host={host}
						navColumns={footerProps.navColumns}
						contact={footerProps.contact}
						socialLinks={footerProps.socialLinks}
						legalLinks={footerProps.legalLinks}
					/>

					{/* Portal for React Datepicker */}
					<DatePickerWrapper sx={{ zIndex: 11 }}>
						<Box id={React.useId()}></Box>
					</DatePickerWrapper>
				</MainContentWrapper>
			</VerticalLayoutWrapper>

			{/* Scroll to top button */}
			{scrollToTop ? (
				scrollToTop(props)
			) : (
				<ScrollToTop>
					<Fab color="primary" size="small" aria-label="scroll back to top" sx={{ borderRadius: 0 }}>
						<KeyboardArrowUpIcon />
					</Fab>
				</ScrollToTop>
			)}
		</React.Fragment>
	);
};

export default VerticalLayout;
